import { useState } from 'react';
import { CalendarClock, Zap, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Expense } from '../../types';
import { RECURRING_TYPE_META } from '../../types';
import { getCategoryMeta } from '../../hooks/useAnalytics';
import { nextDueLabel, nextDueUrgency } from '../../lib/recurring';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

interface UpcomingRecurringProps {
  templates: Expense[];
  loading: boolean;
  onLogNow: (template: Expense) => Promise<void>;
  onDisableRecurring: (template: Expense) => Promise<void>;
}

const urgencyClasses = {
  overdue:  'border-red-500/40 bg-red-500/5',
  today:    'border-amber-500/40 bg-amber-500/5',
  soon:     'border-brand-500/30 bg-brand-500/5',
  upcoming: 'border-slate-700 bg-slate-800/30',
} as const;

const urgencyBadge = {
  overdue:  'bg-red-500/20 text-red-400',
  today:    'bg-amber-500/20 text-amber-400',
  soon:     'bg-brand-500/20 text-brand-400',
  upcoming: 'bg-slate-700 text-slate-400',
} as const;

export default function UpcomingRecurring({
  templates,
  loading,
  onLogNow,
  onDisableRecurring,
}: UpcomingRecurringProps) {
  const [loggingId, setLoggingId] = useState<string | null>(null);
  const [disablingId, setDisablingId] = useState<string | null>(null);

  const handleLogNow = async (template: Expense) => {
    setLoggingId(template.id);
    try {
      await onLogNow(template);
      toast.success(`${template.description} loggato ✓`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante il log');
    } finally {
      setLoggingId(null);
    }
  };

  const handleDisable = async (template: Expense) => {
    setDisablingId(template.id);
    try {
      await onDisableRecurring(template);
      toast.success(`${template.description} non e piu ricorrente`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore disattivazione ricorrenza');
    } finally {
      setDisablingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="card p-8 flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
          <CalendarClock size={22} className="text-slate-600" />
        </div>
        <div>
          <p className="text-slate-400 text-sm font-medium">Nessuna scadenza nei prossimi 30 giorni</p>
          <p className="text-slate-600 text-xs mt-1">
            Aggiungi una spesa ricorrente per vederla qui.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {templates.map(template => {
          const meta      = getCategoryMeta(template.category || "");
        const urgency   = nextDueUrgency(template.next_due!);
        const dueLabel  = nextDueLabel(template.next_due!);
        const recMeta   = RECURRING_TYPE_META[template.recurring_type];
        const isLogging = loggingId === template.id;
        const isDisabling = disablingId === template.id;

        return (
          <div
            key={template.id}
            className={`rounded-xl border p-4 flex items-center gap-4 transition-colors ${urgencyClasses[urgency]}`}
          >
            {/* Category icon */}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: meta.bgColor }}
            >
              {meta.icon}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-slate-100 truncate">
                  {template.description}
                </p>
                {/* Recurring type badge */}
                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md bg-slate-800 text-slate-400">
                  <RefreshCw size={9} />
                  {recMeta.label}
                </span>
                {/* Auto badge for overdue/today */}
                {(urgency === 'overdue' || urgency === 'today') && (
                  <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md font-medium ${urgencyBadge[urgency]}`}>
                    <Zap size={9} />
                    {urgency === 'overdue' ? 'Scaduta' : 'Oggi'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-slate-500">{dueLabel}</span>
                <span className="text-xs text-slate-600">·</span>
                <span
                  className="text-xs font-medium px-1.5 py-0.5 rounded-md"
                  style={{ color: meta.color, background: meta.bgColor }}
                >
                  {meta.label}
                </span>
                {template.next_due && (
                  <>
                    <span className="text-xs text-slate-600">·</span>
                    <span className="text-xs text-slate-500">
                      {format(parseISO(template.next_due), 'd MMM yyyy', { locale: it })}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Amount */}
            <p className="text-base font-semibold text-slate-100 flex-shrink-0">
              €{Number(template.amount).toFixed(2)}
            </p>

            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={() => handleDisable(template)}
                disabled={isDisabling}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isDisabling ? <RefreshCw size={12} className="animate-spin" /> : <span>✕</span>}
                <span className="hidden sm:inline">Disabilita</span>
              </button>
              <button
                onClick={() => handleLogNow(template)}
                disabled={isLogging}
                className={[
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                  'bg-brand-600 hover:bg-brand-500 text-white disabled:opacity-60 disabled:cursor-not-allowed',
                  urgency === 'overdue' ? 'ring-1 ring-red-400/50' : '',
                ].join(' ')}
              >
                {isLogging ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Zap size={12} />
                )}
                <span className="hidden sm:inline">Logga ora</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
